import React, { useState } from 'react';
import { Search, MapPin, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrackSection = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [routeResults, setRouteResults] = useState(null);
  const [loadingTrack, setLoadingTrack] = useState(false);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const { toast } = useToast();

  const handleTrack = async () => {
    console.log('handleTrack called with:', trackingNumber);
    
    if (!trackingNumber.trim()) {
      toast({
        title: "Error",
        description: "Please enter a tracking number",
        variant: "destructive"
      });
      return;
    }

    setLoadingTrack(true);
    try {
      console.log('Making API call to:', `${API}/tracking/track`);
      const response = await axios.post(`${API}/tracking/track`, {
        tracking_number: trackingNumber
      });
      
      console.log('API Response:', response.data);
      
      if (response.data.success) {
        setTrackingResult(response.data);
        toast({
          title: "Success",
          description: "Shipment found!"
        });
      } else {
        setTrackingResult(null);
        toast({
          title: "Not Found",
          description: response.data.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Tracking error:', error);
      toast({
        title: "Error",
        description: "Failed to track shipment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingTrack(false);
    }
  };

  const handleSearch = async () => {
    console.log('handleSearch called with:', origin, destination);
    
    if (!origin.trim() || !destination.trim()) {
      toast({
        title: "Error",
        description: "Please enter both origin and destination",
        variant: "destructive"
      });
      return;
    }

    setLoadingRoute(true);
    try {
      console.log('Making API call to:', `${API}/routes/search`);
      const response = await axios.post(`${API}/routes/search`, {
        origin,
        destination
      });
      
      console.log('API Response:', response.data);
      
      if (response.data.success && response.data.routes.length > 0) {
        setRouteResults(response.data.routes);
        toast({
          title: "Success",
          description: `Found ${response.data.routes.length} route(s)!`
        });
      } else {
        setRouteResults([]);
        toast({
          title: "No Routes Found",
          description: response.data.message || "No routes available for this route",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Route search error:', error);
      toast({
        title: "Error",
        description: "Failed to search routes. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingRoute(false);
    }
  };

  return (
    <section className="bg-white py-12">
      <div className="max-w-[1400px] mx-auto px-6">
        <Tabs defaultValue="track" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-8">
            <TabsTrigger value="track">Track Shipment</TabsTrigger>
            <TabsTrigger value="search">Search Route</TabsTrigger>
          </TabsList>
          
          <TabsContent value="track">
            <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mb-6">
              <div className="flex-1">
                <Input
                  placeholder="B/L, container number or parcel"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
                  className="h-12 text-base"
                />
              </div>
              <Button 
                onClick={handleTrack}
                disabled={loadingTrack}
                className="bg-[#00ACC1] hover:bg-[#0097A7] text-white px-8 h-12"
              >
                {loadingTrack ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Search className="w-5 h-5 mr-2" />
                )}
                Track
              </Button>
            </div>

            {trackingResult && trackingResult.success && (
              <Card className="max-w-3xl">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-2xl font-bold text-[#0A1F3B]">{trackingResult.tracking_number}</h3>
                        <p className="text-gray-600">Tracking Number</p>
                      </div>
                      <div className="text-right">
                        <span className="inline-block px-4 py-2 bg-[#00ACC1] text-white rounded-full font-semibold">
                          {trackingResult.status}
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 pt-4 border-t">
                      <div>
                        <p className="text-sm text-gray-500">Current Location</p>
                        <p className="text-lg font-semibold text-[#0A1F3B]">{trackingResult.current_location}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Estimated Delivery</p>
                        <p className="text-lg font-semibold text-[#0A1F3B]">{trackingResult.estimated_delivery}</p>
                      </div>
                    </div>

                    {trackingResult.timeline && trackingResult.timeline.length > 0 && (
                      <div className="pt-4 border-t">
                        <h4 className="font-semibold text-[#0A1F3B] mb-4">Shipment Timeline</h4>
                        <div className="space-y-3">
                          {trackingResult.timeline.map((event, index) => (
                            <div key={index} className="flex gap-4">
                              <div className="flex-shrink-0 w-3 h-3 bg-[#00ACC1] rounded-full mt-1"></div>
                              <div className="flex-1">
                                <p className="font-semibold text-[#0A1F3B]">{event.status}</p>
                                <p className="text-sm text-gray-600">{event.location}</p>
                                <p className="text-xs text-gray-500">{new Date(event.timestamp).toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="search">
            <div className="flex flex-col lg:flex-row gap-4 max-w-4xl mb-6">
              <div className="flex-1">
                <Input
                  placeholder="From (Origin)"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  className="h-12 text-base"
                />
              </div>
              <div className="flex-1">
                <Input
                  placeholder="To (Destination)"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="h-12 text-base"
                />
              </div>
              <Button 
                onClick={handleSearch}
                disabled={loadingRoute}
                className="bg-[#00ACC1] hover:bg-[#0097A7] text-white px-8 h-12"
              >
                {loadingRoute ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <MapPin className="w-5 h-5 mr-2" />
                )}
                Search
              </Button>
            </div>

            {routeResults && routeResults.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {routeResults.map((route) => (
                  <Card key={route.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="space-y-3">
                        <div className="flex justify-between items-start">
                          <h3 className="text-lg font-bold text-[#0A1F3B]">{route.route_id}</h3>
                          <span className="text-[#00ACC1] font-semibold text-sm">{route.service_type}</span>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Route</p>
                          <p className="font-semibold text-[#0A1F3B]">{route.origin} → {route.destination}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Transit Time</p>
                            <p className="font-semibold text-sm">{route.transit_time}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Price Range</p>
                            <p className="font-semibold text-sm text-[#00ACC1]">{route.price_range}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default TrackSection;